import React from 'react';
import { cn } from '@/utils';
import { Button } from '@/components/Buttons';
import { CardProps } from './Card.types';
import { cardStyles, cardHeaderStyles, cardContentStyles } from './Card.styles';

/**
 * A flexible card component that can display title, subtitle, content, and actions.
 * Designed to be generic and reusable across different use cases.
 *
 * Based on the Figma design for "Next Role-Play" card structure.
 */
export const Card: React.FC<CardProps> = ({
  actions,
  children,
  className,
  contentClassName,
  headerClassName,
  padding = 'medium',
  radius = 'medium',
  subtitle,
  title,
  withBackground = true,
  withBorder = true,
  withContentBorder = false,
}) => {
  return (
    <div
      className={cn(
        cardStyles({
          withBackground,
          withBorder,
          padding,
          radius,
        }),
        className,
      )}
    >
      {/* Header Section - Title and optional content wrapper */}
      {(title || children || subtitle || actions) && (
        <div className={cn(cardHeaderStyles(), headerClassName)}>
          {/* Title */}
          {title && <h2 className="typography-section-subtitle text-neutral-black">{title}</h2>}

          {/* Content Card Container - matches Figma design structure */}
          {(subtitle || actions || children) && (
            <div className={cn(cardContentStyles({ withContentBorder }), contentClassName)}>
              {/* Subtitle/Description */}
              {subtitle && <div className="typography-body text-neutral-black">{subtitle}</div>}

              {/* Children content */}
              {children}

              {/* Actions (buttons, etc.) */}
              {actions && (
                <div className={'flex w-full gap-2'}>
                  {actions?.map((action, index) => (
                    <Button
                      key={index}
                      className={action?.className}
                      disabled={action?.disabled}
                      iconLeft={action?.iconLeft}
                      iconRight={action?.iconRight}
                      iconOnly={action?.iconOnly}
                      label={action?.label}
                      loading={action?.loading}
                      onClick={action?.onClick}
                      size={action?.size}
                      type={action?.type}
                      variant={action?.variant}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

Card.displayName = 'Card';

export default Card;
